<template>
  <div class="auto-month-report" v-loading.fullscreen.lock="loading" element-loading-text="請稍等...">
    <div class="controls">
      <div class="left">
        <span class="range-label">初始值參照時間: </span>
        <el-date-picker
          v-model="selectedYear"
          type="year"
          placeholder="選擇年度"
          :clearable="false"
          format="yyyy"
          value-format="yyyy"
          :picker-options="{ disabledDate: d => d > new Date() }"
        />
        <span class="range-label">報告月份: </span>
        <el-date-picker
          v-model="selectedMonth"
          type="month"
          placeholder="選擇月份"
          :clearable="false"
          format="yyyy/MM"
          value-format="yyyy-MM"
          :picker-options="{ disabledDate: d => d > new Date() }"
        />
        <el-button type="primary" size="mini" :loading="loading" @click="run" icon="el-icon-caret-right">
          執行
        </el-button>
        <span class="range-label" v-if="rangeLabel">Time Range: {{ rangeLabel }}</span>
      </div>
      <div class="right">
        <el-button
          type="info"
          size="mini"
          :disabled="!chartOptions"
          icon="el-icon-document"
          @click="exportRainDataCsv"
        >
          下載雨量數據(CSV)
        </el-button>
        <el-button
          type="success"
          size="mini"
          :loading="exporting"
          :disabled="exportDisabled"
          icon="el-icon-download"
          @click="exportDocx"
        >
          Export .docx
        </el-button>
      </div>
    </div>
    <el-alert v-if="error" :title="error" type="error" show-icon class="mb-12" />
    <el-card class="chart-card">
      <v-chart
        v-if="chartOptions"
        ref="reportChart"
        :options="chartOptions"
        autoresize
        style="height: 420px; width: 100%;"
      />
      <div v-else class="chart-placeholder">請選擇月份並點擊執行</div>
    </el-card>

    <el-card class="chart-card" style="margin-top: 16px;" v-if="chartOptionsDailyMax">
      <v-chart
        ref="reportChartDaily"
        :options="chartOptionsDailyMax"
        autoresize
        style="height: 420px; width: 100%;"
      />
    </el-card>

    <!-- ELP 對應 RAIN 月度統計表 -->
    <el-card class="chart-card elp-table" style="margin-top: 16px;">
      <div class="chart-title" style="font-weight: 600; margin-bottom:8px;">水壓計水位變化彙整表</div>
      <el-table :data="elpRows" size="mini" border :header-cell-style="{background:'#dbe7f4',fontWeight:600}">
        <el-table-column prop="position" label="儀器位置" width="180"/>
        <el-table-column :label="`${monthLabel}累計雨量(mm)`" prop="rainTotal" width="140"/>
        <el-table-column label="水位高程(GL.)">
          <el-table-column prop="wlMax" label="最高" width="100">
            <template #default="scope"><span class="num">{{ scope.row.wlMax }}</span></template>
          </el-table-column>
          <el-table-column prop="wlMin" label="最低" width="100">
            <template #default="scope"><span class="num">{{ scope.row.wlMin }}</span></template>
          </el-table-column>
          <el-table-column :label="lastLabel || '最後'" prop="wlLast" width="120">
            <template #default="scope"><span class="num">{{ scope.row.wlLast }}</span></template>
          </el-table-column>
        </el-table-column>
        <el-table-column :label="`${monthLabel}水位變化(m)`">
          <el-table-column prop="trend" label="上升/下降" width="100">
            <template #default="scope">
              <span :class="scope.row.trend === '上升' ? 'rise' : (scope.row.trend === '下降' ? 'fall' : '')">{{ scope.row.trend }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="delta" label="變化量" width="100">
            <template #default="scope">
              <span
                class="num"
                :class="{ negative: parseFloat(scope.row.delta) < 0, positive: parseFloat(scope.row.delta) >= 0 }"
              >
                {{ scope.row.delta }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="wlRange" label="本月最大水位變動量(m)" width="160">
            <template #default="scope">
              <span class="num">{{ (scope.row.wlMax - scope.row.wlMin).toFixed(2) }}</span>
            </template>
          </el-table-column>
        </el-table-column>
      </el-table>
      <div class="tm-table">
        <div class="tm-table-title">各自動監測站地滑計數值變化比較</div>
        <table class="tm-summary-table" ref="tmTable">
          <thead>
            <!-- 第一層：期間 / 備註 -->
            <tr>
              <th rowspan="3">標別</th>
              <th rowspan="3">自動站里程</th>
              <th rowspan="3">儀器編號</th>
              <th colspan="8">{{ tmReferenceLabelfull }} ~ {{ tmLastValidLabel || tmCurrLabel }}</th>
              <th colspan="7">備註</th>
            </tr>

            <!-- 第二層：群組標題 -->
            <tr>
              <!-- 左半段 -->
              <th colspan="3">地滑計讀值(mm)</th>
              <th colspan="2">管理值檢核</th>
              <th colspan="3">總計變位量(mm)</th>
              <!-- 右半段 -->
              <th colspan="3">{{ selectedYear }}年1月迄今~總計變位量達</th>
              <th colspan="3">本月總計變位量達</th>
              <th rowspan="2">註記</th>
            </tr>

            <!-- 第三層：實際欄位 -->
            <tr>
              <!-- 地滑計讀值 -->
              <th>{{ tmReferenceLabel }}</th>
              <th>{{ tmPrevLabel }}</th>
              <th>{{ tmCurrLabel }}</th>

              <!-- 管理值檢核 -->
              <th>注意值<br>(±30mm)</th>
              <th>行動值<br>(>10mm/hr)</th>

              <!-- 總計變位量 -->
              <th>{{ tmPrevLabel }}</th>
              <th>{{ tmCurrLabel }}</th>
              <th>本月變化</th>

              <!-- XXXX年1月迄今 -->
              <th>5~10mm</th>
              <th>10~20mm</th>
              <th>>20mm</th>

              <!-- 本月總計 -->
              <th>5~10mm</th>
              <th>10~20mm</th>
              <th>>20mm</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in tmTableRows" :key="row.key">
              <td v-if="row.showLabel" :rowspan="row.labelRowSpan">{{ labelMap(row.label) }}</td>
              <td v-if="row.showStation" :rowspan="row.stationRowSpan">{{ row.stationName || '-' }}</td>
              <td>{{ row.instrumentName || '-' }}</td>
              <td>{{ row.referenceValue }}</td>
              <td>{{ row.prevSnapshotDisplay }}</td>
              <td>{{ row.currSnapshotDisplay }}</td>
              <td>{{ row.alert30 }}</td>
              <td></td>
              <td>{{ row.prevSnapshotDiff }}</td>
              <td>{{ row.currSnapshotDiff }}</td>
              <td>{{ row.currVsPrevDiff }}</td>
              <td>{{ row.bucket510 }}</td>
              <td>{{ row.bucket1020 }}</td>
              <td>{{ row.bucket20 }}</td>
              <td>{{ row.monthBucket510 }}</td>
              <td>{{ row.monthBucket1020 }}</td>
              <td>{{ row.monthBucket20 }}</td>
              <td>{{ row.remark }}</td>
            </tr>
            <tr v-if="!tmTableRows.length">
              <td colspan="18" class="empty">暫無資料</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="tm-summary-stats">
        <div class="tm-table-title">地滑計變位量統計表</div>
        <table class="tm-summary-table">
          <thead>
            <tr>
              <th rowspan="1">項目</th>
              <th colspan="3">{{ tmReferenceLabelfull }} ~ {{ tmLastValidLabel || tmCurrLabel }}<br>總計變位量達</th>
              <th colspan="3">{{ tmLastValidLabel || tmCurrLabel }} 單月總計變位量達<br>(與 {{ tmPrevLabel }} 相比)</th>
            </tr>
            <tr>
              <th>地滑計變位量(mm)</th>
              <th>5~10mm</th>
              <th>10~20mm</th>
              <th>>20mm</th>
              <th>5~10mm</th>
              <th>10~20mm</th>
              <th>>20mm</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>地滑計數量</td>
              <td>{{ tmSummaryStats.total510 }}</td>
              <td>{{ tmSummaryStats.total1020 }}</td>
              <td>{{ tmSummaryStats.total20 }}</td>
              <td>{{ tmSummaryStats.month510 }}</td>
              <td>{{ tmSummaryStats.month1020 }}</td>
              <td>{{ tmSummaryStats.month20 }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </el-card>
  </div>
  
</template>

<script>
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  VerticalAlign,
  ImageRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
} from 'docx';

import * as XLSX from 'xlsx';

export default {
  name: 'monthlyReport',
  data() {
    const now = new Date();

    const lastDayOfThisMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const isLastDay = now.getDate() === lastDayOfThisMonth;

    // 【核心邏輯修正】
    // 取得「上個月」的 Date 物件
    // 技巧：利用 Date 建構子 new Date(年, 月, 日)。
    // JavaScript 的月份是 0-11，但它接受負值或超過的數值並自動推算。
    // now.getMonth() - 1：如果是 1月(0)，減 1 變成 -1，Date 會自動跳回「去年的 12月」。
    // 固定設為 1 號 (第三個參數)，是為了避免「3月30日減一個月」因二月沒30號而跳錯日期的問題。
    const monthOffset = isLastDay ? 0 : -1;
    const prevMonthDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);

    // 從推算出來的日期物件取得正確的 年 與 月
    const pmYear = prevMonthDate.getFullYear();
    // getMonth() 回傳 0-11，顯示時需要 +1
    const pmMonth = prevMonthDate.getMonth() + 1;

    // 格式化為 YYYY-MM
    const ym = `${pmYear}-${String(pmMonth).padStart(2, '0')}`;

    // 設定初始參照年份
    // 邏輯：讓「初始值參照年份」預設跟著「報告月份的年份」一致
    // 這樣 2026年1月執行時 -> 報告月份 2025-12 -> 參照年份 2025
    const selectedYearStr = String(pmYear - 1);

    return {
      loading: false,
      exporting: false,
      error: '',
      selectedMonth: ym,
      range: [],
      rainInstruments: [],
      elpInstruments: [],
      points: [],
      dailyMaxPoints: [],       // 【新增】日最大雨量點位資料
      rawRainCsvData: '', // 用來儲存後端回傳的原始雨量 CSV
      showLabels: false,
      chartOptions: null,
      chartOptionsDailyMax: null, // 【新增】日最大雨量圖表設定
      elpRows: [],
      tmTableRows: [],
      lastLabel: '',
      areaStationCache: null,
      areaStationProjectId: '',
      areaStationPromise: null,
      selectedYear: selectedYearStr,
      tmReferenceLabel: `${selectedYearStr}/01/01`,
      tmReferenceLabelfull: `${selectedYearStr}/01/01`,
      tmReferenceIso: `${selectedYearStr}-01-01T00:00:00`,
      tmLastValidLabel: '',
      tmLastValidIso: '',
      endTime: '',
    };
  },
  computed: {
    isCurrentMonthLastDay() {
      try {
        if (!this.selectedMonth) return false;
        const [y, m] = this.selectedMonth.split('-').map(n => parseInt(n, 10));
        const now = new Date();
        const lastDayOfThisMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        return (now.getFullYear() === y && (now.getMonth() + 1) === m && now.getDate() === lastDayOfThisMonth);
      } catch (e) { return false; }
    },
    selectedProject() {
      return this.$store.getters['project/selectedProject'];
    },
    timezone() {
      return this.$store.getters['env/timezone'];
    },
    rangeLabel() {
      if (!this.range.length) return '';
      const [s, e] = this.range;
      const fmt = v => this.$moment(v).tz(this.timezone || 'UTC').format('YYYY/MM/DD HH:mm');
      return `${fmt(s)} ~ ${fmt(e)}`;
    },
    exportDisabled() {
      if (this.exporting) return true;
      if (this.loading) return true;
      return !this.chartOptions;
    },
    monthLabel() {
      try {
        const [y, m] = (this.selectedMonth || '').split('-');
        if (!y || !m) return '';
        return `${parseInt(m, 10)}月`;
      } catch (e) { return ''; }
    },
    tmPrevLabel() {
      const info = this.getTmWindows();
      return info?.prevLabel || '本月1日';
    },
    tmCurrLabel() {
      const info = this.getTmWindows();
      return info?.currLabel || '當月X日';
    },
    tmSummaryStats() {
      return this.computeTmSummaryStats();
    },
  },
  watch: {
    selectedYear(newYear) {
      this.tmReferenceLabel = `${newYear}/01`;
      this.tmReferenceLabelfull = `${newYear}/01/01`;
      this.tmReferenceIso = `${newYear}-01-01T00:00:00`;
    },
    selectedMonth() {
      this.updateMonthRange();
      this.tmTableRows = [];
    },
    showLabels() {
      if (this.points.length) this.buildChart();
    },
  },
  mounted() {
    this.updateMonthRange();
    this.loadStations();
    this.loadElpList();
  },
  methods: {
    async fetchInstrumentRemark(instrument_id) {
      try {
        const res = await this.$axios({
          category: 'project',
          method: 'post',
          apiName: `${this.selectedProject._id}/instrument/remark?type=all`,
          data: { instrument: instrument_id },
        });
        
        if (res.data && Array.isArray(res.data) && res.data.length > 0) {
          // 1. 篩選出 type 為 'report' 的所有註記
          const reportRemarks = res.data.filter(item => item.type === 'report');
          
          if (reportRemarks.length > 0) {
            // 2. 依照 start_date 進行降冪排序 (最新的時間在最前面)
            // 若沒有 start_date 則預設為 0 (最舊)
            reportRemarks.sort((a, b) => {
              const dateA = new Date(a.start_date || 0).getTime();
              const dateB = new Date(b.start_date || 0).getTime();
              return dateB - dateA;
            });

            // 3. 回傳最新一筆 (index 0) 的 content
            return reportRemarks[0].content || '';
          }
        }
        return ''; // 若無資料或沒有 type='report' 的項目，回傳空字串
      } catch (error) {
        console.error('取得儀器註記失敗:', instrument_id, error);
        return '';
      }
    },
    // ======== 新增匯出 CSV 方法 ========
    exportRainDataCsv() {
      // 檢查是否有原始資料
      if (!this.rawRainCsvData) {
        this.$message?.warning?.('請先點擊執行以取得雨量資料');
        return;
      }

      // 加上 BOM (\uFEFF) 確保 Excel 開啟時中文不會亂碼，並接上原始 CSV 字串
      const csvContent = '\uFEFF' + this.rawRainCsvData;

      // 建立 Blob 與下載連結
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const filename = `自動化監測站雨量原始數據_${this.$moment().tz(this.timezone || 'UTC').format('YYYYMMDD_HHmmss')}.csv`;
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    computeTmSummaryStats() {
      const stats = {
        total510: 0, total1020: 0, total20: 0,
        month510: 0, month1020: 0, month20: 0,
      };
      this.tmTableRows.forEach(r => {
        if (r.bucket510 === '●') stats.total510++;
        if (r.bucket1020 === '●') stats.total1020++;
        if (r.bucket20 === '●') stats.total20++;
        if (r.monthBucket510 === '●') stats.month510++;
        if (r.monthBucket1020 === '●') stats.month1020++;
        if (r.monthBucket20 === '●') stats.month20++;
      });
      return stats;
    },
    labelMap(label) {
      const map = {
        'TK028+470-TK028+525': 'C215',
        'TK086+320-TK086+420': 'C230',
        'TK088+224-TK088+511': 'C230',
        'TK093+100-TK093+200': 'C230',
        'TK093+541-TK093+670': 'C230',
        'TK093+755-TK093+945': 'C230',
        'TK096+855-TK097+015': 'C230',
        'TK098+770-TK098+960': 'C230',
        'TK099+812-TK099+912': 'C230',
        'TK100+212-TK100+365': 'C230',
        'TK107+100-TK107+200': 'C230',
        'TK117+470-TK117+600': 'C240',
        'TK118+700-TK118+900': 'C240',
        'TK123+440-TK123+590': 'C240',
        'TK125+800-TK125+940': 'C240',
        'TK125+940-TK126+266': 'C240',
        'TK171+880-TK172+005': 'C260',
        'TK172+580-TK172+900': 'C260',
      };
      return map[label] || label; // 若沒有對照則顯示原值
    },
    updateMonthRange() {
      try {
        if (!this.selectedMonth) return;
        const [y, m] = this.selectedMonth.split('-').map(n => parseInt(n, 10));
        const start = new Date(y, m - 1, 1, 0, 0, 0, 0);
        // const end = new Date(y, m, 0, 23, 59, 0, 0); // 該月最後一天 23:59

        let end;
        if (this.isCurrentMonthLastDay) {
          end = new Date(); // 當月最後一天則抓到現在這一刻
        } else {
          end = new Date(y, m, 1, 0, 0, 0, 0);
        }
        this.range = [start.toISOString(), end.toISOString()];
      } catch (_e) {
        this.range = [];
      }
    },
    // 解析里程標 (e.g., TK93+889 => value: 93.889)
    parseMileage(source = '') {
      const match = /TK\s*(\d+)(?:\s*\+\s*(\d+))?/i.exec(source);
      if (!match) return { value: Number.POSITIVE_INFINITY, tag: '', km: null, meter: null };
      const km = parseInt(match[1], 10);
      const meter = match[2] ? parseInt(match[2], 10) : 0;
      return { value: km + meter / 1000, tag: `TK${km}${meter ? `+${meter}` : ''}`, km, meter };
    },
    isMileageInRange(v) { return Number.isFinite(v) && v >= 28 && v <= 173; },
    detectELP(instrument, stationName = '') {
      const sources = [
        instrument?.latestVersion?.name,
      ].map(t => String(t || '').toUpperCase());
      return sources.some(s => s && s.includes('ELP') && !s.includes('('));
    },
    detectRain(instrument, stationName = '') {
      const sources = [
        instrument?.latestVersion?.name,
        instrument?.name,
        instrument?.instrument_name,
      ].map(t => String(t || '').toUpperCase());

      const isRain = sources.some(s => s && (s.includes('RAIN') || s.includes('RG')));
      
      const isExtraInfo = sources.some(s => 
        s.includes('BATT') || 
        s.includes('RSSI'),
      );

      return isRain && !isExtraInfo;
    },
    detectTM(instrument) {
      const sources = [
        instrument?.instrument_type?.name,
        instrument?.instrument_type?.code,
        instrument?.latestVersion?.name,
        instrument?.name,
        instrument?.instrument_name,
      ].map(t => String(t || '').toUpperCase());
      return sources.some(s => s && s.includes('TM'));
    },
    async fetchAreaStationTree(force = false) {
      const project = this.selectedProject;
      if (!project || !project._id) return [];
      const projectId = project._id;
      const sameProject = this.areaStationProjectId === projectId;
      if (!force && sameProject && Array.isArray(this.areaStationCache)) {
        return this.areaStationCache;
      }
      if (!force && sameProject && this.areaStationPromise) {
        return this.areaStationPromise;
      }
      const request = this.$axios({
        category: 'project',
        method: 'get',
        apiName: `${projectId}/area-station-instrument`,
      });
      const fetchPromise = request.then(res => {
        const areas = res.data?.area || [];
        if (this.areaStationProjectId === projectId) {
          this.areaStationCache = areas;
        }
        if (this.areaStationPromise === fetchPromise) {
          this.areaStationPromise = null;
        }
        return areas;
      }).catch(err => {
        if (this.areaStationProjectId === projectId) {
          this.areaStationCache = null;
        }
        if (this.areaStationPromise === fetchPromise) {
          this.areaStationPromise = null;
        }
        throw err;
      });
      this.areaStationProjectId = projectId;
      this.areaStationPromise = fetchPromise;
      return fetchPromise;
    },
    async loadStations(force = false) {
      const project = this.selectedProject;
      if (!project || !project._id) return;
      this.loading = true;
      this.error = '';
      try {
        const areas = await this.fetchAreaStationTree(force) || [];
        const list = [];
        areas.forEach(area => {
          (area.station || []).forEach(station => {
            (station.instrument || []).forEach(ins => {
              if (!this.detectRain(ins, station.name)) return;
              const rawName = ins.latestVersion?.name || ins.name || ins.instrument_name || ins._id;
              const m = this.parseMileage(rawName || station.name || '');
              const stationM = this.parseMileage(station.name || '');
              const value = Number.isFinite(m.value) ? m.value : stationM.value;
              const tag = m.tag || stationM.tag || '';
              if (!this.isMileageInRange(value)) return;
              list.push({ id: ins._id, name: rawName, mileageValue: value, mileageTag: tag });
            });
          });
        });
        // 去除重複儀器
        const seen = new Set();
        const uniq = list.filter(x => { if (!x.id || seen.has(x.id)) return false; seen.add(x.id); return true; });
        // 依里程排序
        uniq.sort((a, b) => (a.mileageValue ?? Infinity) - (b.mileageValue ?? Infinity));
        this.rainInstruments = uniq;
      } catch (e) {
        console.error(e);
        this.error = '讀取站點失敗';
      } finally {
        this.loading = false;
      }
    },
    async loadElpList(force = false) {
      const project = this.selectedProject;
      if (!project || !project._id) return;
      try {
        const areas = await this.fetchAreaStationTree(force) || [];
        const elp = [];
        areas.forEach(area => {
          (area.station || []).forEach(station => {
            (station.instrument || []).forEach(ins => {
              if (!this.detectELP(ins, station.name)) return;
              const rawName = ins.latestVersion?.name || ins.name || ins.instrument_name || ins._id;
              const m = this.parseMileage(rawName || station.name || '');
              const stationM = this.parseMileage(station.name || '');
              const value = Number.isFinite(m.value) ? m.value : stationM.value;
              const tag = m.tag || stationM.tag || '';
              if (!this.isMileageInRange(value)) return;
              elp.push({ id: ins._id, name: rawName, mileageValue: value, mileageTag: tag });
            });
          });
        });
        const seen = new Set();
        this.elpInstruments = elp.filter(x => { if (!x.id || seen.has(x.id)) return false; seen.add(x.id); return true; })
          .sort((a,b) => (a.mileageValue ?? Infinity) - (b.mileageValue ?? Infinity));
      } catch (_) { /* ignore */ }
    },
    async loadTmTable(force = false) {
      const project = this.selectedProject;
      if (!project || !project._id) return;
      const windows = this.getTmWindows();
      if (!windows) return;
      try {
        const areas = await this.fetchAreaStationTree(force) || [];
        const rows = [];
        const seen = new Set();
        areas.forEach(area => {
          const areaName = (area?.name || '').trim();
          const areaLabel = (area?.label || areaName || '').trim();
          if (areaName === '維護資訊' || areaLabel === '維護資訊') return;
          (area.station || []).forEach(station => {
            const stationName = (station?.name || '').trim();
            const stationId = station?._id || station?.id || stationName;
            const stationMileage = this.parseMileage(stationName);
            const mileageValue = Number.isFinite(stationMileage.value) ? stationMileage.value : Number.POSITIVE_INFINITY;
            (station.instrument || []).forEach(ins => {
              if (!this.detectTM(ins)) return;
              const rawName = ins.latestVersion?.name || ins.name || ins.instrument_name || ins._id;
              const instrumentId = ins?._id || rawName;
              const instrumentKey = this.normalizeHeaderName(rawName);
              const uniqueKey = `${stationId}-${instrumentId}`;
              if (seen.has(uniqueKey)) return;
              seen.add(uniqueKey);
              rows.push({
                key: uniqueKey,
                label: areaLabel,
                stationName,
                stationId,
                instrumentName: rawName,
                instrumentKey,
                instrumentId,
                mileageValue,
              });
            });
          });
        });
        if (!rows.length) {
          this.tmTableRows = [];
          return;
        }
        rows.sort((a, b) => {
          const labelA = String(a.label || '');
          const labelB = String(b.label || '');
          if (labelA !== labelB) return labelA.localeCompare(labelB, undefined, { numeric: true, sensitivity: 'base' });
          if (a.mileageValue !== b.mileageValue) return a.mileageValue - b.mileageValue;
          return String(a.instrumentName || '').localeCompare(String(b.instrumentName || ''), undefined, { numeric: true, sensitivity: 'base' });
        });
        const instrumentIds = Array.from(new Set(rows.map(row => row.instrumentId).filter(Boolean)));
        let referenceValues = {};
        let monthlySnapshots = {};
        let remarksMap = {};

        if (instrumentIds.length) {
          // ===== [修改] 加入 Promise.all 批次呼叫 fetchInstrumentRemark =====
          const [ref, snaps, remarksArray] = await Promise.all([
            this.fetchTmReferenceValues(instrumentIds),
            this.fetchTmMonthlySnapshots(instrumentIds, windows),
            Promise.all(instrumentIds.map(async (id) => {
              const text = await this.fetchInstrumentRemark(id);
              return { id, text };
            })),
          ]);
          referenceValues = ref;
          monthlySnapshots = snaps;
          
          // 將取得的註記整理進 Map 中
          remarksArray.forEach(item => {
            remarksMap[item.id] = item.text;
          });
          // =============================================================
        }

        const grouped = new Map();
        const order = [];
        rows.forEach(row => {
          const groupKey = `${row.label || ''}__${row.stationId || row.stationName}`;
          if (!grouped.has(groupKey)) {
            grouped.set(groupKey, []);
            order.push(groupKey);
          }
          grouped.get(groupKey).push(row);
        });
        const displayRows = [];
        order.forEach(groupKey => {
          const group = grouped.get(groupKey) || [];
          group.forEach((row, idx) => {
            const valueKey = row.instrumentKey || this.normalizeHeaderName(row.instrumentName);
            const monthly = monthlySnapshots[valueKey] || {};
            const referenceRaw = referenceValues[valueKey];
            const prevEntry = monthly.prev;
            const currEntry = monthly.curr;
            const prevDiff = this.formatTmDiff(referenceRaw, prevEntry);
            const currDiff = this.formatTmDiff(referenceRaw, currEntry);
            const currVsPrevDiff = this.formatTmChange(currEntry, prevEntry);
            const refBuckets = this.getBucketFlags(currDiff);
            const monthBuckets = this.getBucketFlags(currVsPrevDiff);
            const currDisplay = this.formatTmSnapshotDisplay(currEntry, true);
            let alert30 = '';
            if (currDisplay !== 'NAN' && !Number.isNaN(Number(currDisplay)) && Math.abs(Number(currDisplay)) > 30) {
              alert30 = '●';
            }
            displayRows.push({
              key: row.key,
              label: row.label,
              stationName: row.stationName.split('-')[0] || row.stationName,
              instrumentName: row.instrumentName,
              referenceValue: this.formatTmReferenceValue(referenceRaw),
              prevSnapshotDisplay: this.formatTmSnapshotDisplay(prevEntry),
              currSnapshotDisplay: currDisplay,
              alert30,
              prevSnapshotDiff: prevDiff,
              currSnapshotDiff: currDiff,
              currVsPrevDiff,
              bucket510: refBuckets.bucket510,
              bucket1020: refBuckets.bucket1020,
              bucket20: refBuckets.bucket20,
              monthBucket510: monthBuckets.bucket510,
              monthBucket1020: monthBuckets.bucket1020,
              monthBucket20: monthBuckets.bucket20,
              remark: remarksMap[row.instrumentId] || '',
              showLabel: idx === 0,
              showStation: idx === 0,
              labelRowSpan: group.length,
              stationRowSpan: group.length,
            });
          });
        });

        // ----------------------------------------------------
        // 強制安插「TK126+168E-TM-02」空資料
        // 尋找目標上一行的索引，尋找邏輯：儀器名稱包含 '126+115' 且包含 'TM-02'
        const targetIndex = displayRows.findIndex(
          row => (row.instrumentName || '').includes('126+115') && (row.instrumentName || '').includes('TM-02'),
        );

        if (targetIndex !== -1) {
          const targetRow = displayRows[targetIndex];
          
          // 建立一筆全是空值的假資料
          const fakeRow = {
            key: 'fake-TK126+168E-TM-02',
            label: targetRow.label, // 繼承上一行的標別
            stationName: targetRow.stationName, // 繼承上一行的站點里程
            instrumentName: 'TK126+168E-TM-02',
            referenceValue: '',
            prevSnapshotDisplay: '',
            currSnapshotDisplay: '',
            alert30: '',
            prevSnapshotDiff: '',
            currSnapshotDiff: '',
            currVsPrevDiff: '',
            bucket510: '',
            bucket1020: '',
            bucket20: '',
            monthBucket510: '',
            monthBucket1020: '',
            monthBucket20: '',
            remark: '',
            showLabel: false, // 因為被合併了，所以不顯示
            showStation: false, // 因為被合併了，所以不顯示
            labelRowSpan: 0,
            stationRowSpan: 0,
          };

          // 尋找這個群組(相同 stationName)的第一筆資料，也就是帶有 rowspan 的那筆
          // 為了確保合併儲存格不會破版，必須把它的 rowspan + 1
          let firstRowOfGroup = null;
          // 往回找最近的 showStation 為 true 的那一筆
          for (let i = targetIndex; i >= 0; i--) {
            if (displayRows[i].showStation) {
              firstRowOfGroup = displayRows[i];
              break;
            }
          }

          if (firstRowOfGroup) {
            firstRowOfGroup.labelRowSpan = (firstRowOfGroup.labelRowSpan || 1) + 1;
            firstRowOfGroup.stationRowSpan = (firstRowOfGroup.stationRowSpan || 1) + 1;
          }

          // 利用 splice 將假資料插入到目標行的下一行 (targetIndex + 1)
          displayRows.splice(targetIndex + 1, 0, fakeRow);
        }
        // ----------------------------------------------------

        this.tmTableRows = displayRows;
      } catch (error) {
        console.error(error);
        this.tmTableRows = [];
      }
    },
    getTmWindows() {
      try {
        if (!this.selectedMonth) return null;
        const tz = this.timezone || 'UTC';
        const base = this.$moment.tz(`${this.selectedMonth}-01`, tz);
        if (!base || !base.isValid()) return null;

        const currStart = base.clone().startOf('month').hour(0).minute(0).second(0).millisecond(0);

        let currEnd;
        if (this.isCurrentMonthLastDay) {
          currEnd = this.$moment().tz(tz);
        } else {
          currEnd = base.clone().add(1, 'month').startOf('month');
        }

        const prevStart = currStart.clone().subtract(1, 'day').hour(0).minute(0).second(0).millisecond(0);
        const prevEnd = currStart.clone();

        return {
          prevStart,
          prevEnd,
          currStart,
          currEnd,
          rangeStartIso: prevStart.toISOString(),
          rangeEndIso: currEnd.toISOString(),
          // 原本: `${prevStart.year()}/${String(prevStart.month() + 1).padStart(2, '0')}`
          // 改為: 使用 format 輸出完整日期
          prevLabel: prevEnd.format('YYYY/MM/DD'),
          
          // 原本: `${currStart.year()}/${String(currStart.month() + 1).padStart(2, '0')}`
          // 改為: 使用 format 輸出完整日期
          currLabel: currEnd.format('YYYY/MM/DD'),
        };
      } catch (_e) {
        return null;
      }
    },
    async fetchTmMonthlySnapshots(instrumentIds = [], windows = null) {
      const project = this.selectedProject;
      if (!project || !project._id || !instrumentIds.length) return {};
      const win = windows || this.getTmWindows();
      if (!win) return {};
      try {
        const res = await this.$axios({
          category: 'project',
          method: 'post',
          apiName: `${project._id}/output/instrument-export`,
          data: {
            instrument: instrumentIds,
            type: 'measure',
            interval: 'all',
            date: [win.rangeStartIso, win.rangeEndIso],
          },
        });
        const csv = typeof res.data === 'string' ? res.data : '';
        return this.extractMonthlySnapshots(csv, win);
      } catch (error) {
        console.error(error);
        return {};
      }
    },
    extractMonthlySnapshots(csv = '', windows = null) {
      if (!windows) return {};
      const lines = String(csv || '').split(/\r?\n/).filter(line => line.trim().length);
      if (!lines.length) return {};
      const headers = lines[0].split(',').map(h => this.normalizeHeaderName(h));
      const snapshots = {};
      const ensure = (key) => (snapshots[key] = snapshots[key] || { prev: null, curr: null });
      const tz = this.timezone || 'UTC';

      const lastDayDateStr = this.$moment(windows.currEnd).format('YYYY-MM-DD');
      
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',');
        const tsRaw = this.normalizeHeaderName(cols[0] || '');
        if (!tsRaw) continue;
        let tsMoment = this.$moment(tsRaw);
        if (tsMoment && typeof tsMoment.tz === 'function') tsMoment = tsMoment.tz(tz);
        if (!tsMoment || !tsMoment.isValid()) continue;

        const tsValue = tsMoment.valueOf();
        const dateStr = tsMoment.format('YYYY-MM-DD');

        const inPrev = tsMoment.isSameOrAfter(windows.prevStart) && tsMoment.isSameOrBefore(windows.prevEnd);
        const inCurr = tsMoment.isSameOrAfter(windows.currStart) && tsMoment.isSameOrBefore(windows.currEnd);
        if (!inPrev && !inCurr) continue;

        for (let c = 1; c < headers.length && c < cols.length; c++) {
          const key = headers[c];
          if (!key) continue;
          const val = this.normalizeHeaderName(cols[c]);
          if (val === '') continue;
          const bucket = ensure(key);

          // 1. 上個月最後一筆（維持原邏輯，抓該區間最新）
          if (inPrev && (!bucket.prev || tsValue > bucket.prev.tsValue)) {
            bucket.prev = { ts: tsMoment.toISOString(), tsValue, value: val };
          }
          
          // 2. 本月最後一筆（區分月底執行 vs 月初執行）
          if (inCurr) {
            if (this.isCurrentMonthLastDay) {
              // 如果到了最後一天，我們只抓當天 (e.g., 6/30) 且是最靠近 00:00 的第一筆數據
              if (dateStr === lastDayDateStr) {
                if (!bucket.curr || tsValue < bucket.curr.tsValue) {
                  bucket.curr = { ts: tsMoment.toISOString(), tsValue, value: val };
                }
              }
            } else {
              // 維持舊邏輯，抓整個歷史區間內最新（即 7/1 00:00 歷史快照）
              if (!bucket.curr || tsValue > bucket.curr.tsValue) {
                bucket.curr = { ts: tsMoment.toISOString(), tsValue, value: val };
              }
            }

            // 更新最後有效標籤時間基準
            if (bucket.curr && (!this.tmLastValidIso || bucket.curr.tsValue > this.$moment(this.tmLastValidIso).valueOf())) {
              this.tmLastValidIso = bucket.curr.ts;
              this.tmLastValidLabel = this.$moment(bucket.curr.ts).tz(tz).format('YYYY/MM/DD');
            }
          }
        }
      }
      return snapshots;
    },
    formatTmSnapshotDisplay(entry, check=false) {
      if (!entry || !entry.ts) return 'NAN';
      let tsMoment = this.$moment(entry.ts);
      if (tsMoment && typeof tsMoment.tz === 'function') tsMoment = tsMoment.tz(this.timezone || 'UTC');
      const tsLabel = tsMoment && tsMoment.isValid() ? tsMoment.format('YYYY/MM/DD HH:mm') : '';
      const num = Number(entry.value);
      const valueLabel = Number.isNaN(num) ? (entry.value || 'NAN') : num.toFixed(2);
      if (!tsLabel) return valueLabel || 'NAN';
      // return `${tsLabel} (${valueLabel || 'NAN'})`;

      if (check) {
        let isMatch = false;
        if (this.isCurrentMonthLastDay) {
          isMatch = this.$moment(entry.ts).isSame(this.range[1], 'day');
        } else {
          isMatch = (entry.ts === this.range[1]);
        }
        if (!isMatch) {
          return 'NAN';
        }
      }

      return `${valueLabel || 'NAN'}`;
    },
    formatTmDiff(referenceValueRaw, entry) {
      const refNum = Number(referenceValueRaw);
      const entryNum = (entry && entry.value != null) ? Number(entry.value) : NaN;
      if (Number.isNaN(refNum) || Number.isNaN(entryNum)) return 'NAN';
      const diff = entryNum - refNum;
      return diff.toFixed(2);
    },
    formatTmChange(currEntry, prevEntry) {
      const currNum = (currEntry && currEntry.value != null) ? Number(currEntry.value) : NaN;
      const prevNum = (prevEntry && prevEntry.value != null) ? Number(prevEntry.value) : NaN;
      if (Number.isNaN(currNum) || Number.isNaN(prevNum)) return 'NAN';
      return (currNum - prevNum).toFixed(2);
    },
    getBucketFlags(diffStr) {
      const flags = { bucket510: '', bucket1020: '', bucket20: '' };
      if (diffStr == null || diffStr === 'NAN') return flags;
      const diff = Number(diffStr);
      if (Number.isNaN(diff)) return flags;
      const absDiff = Math.abs(diff);
      if (absDiff >= 5 && absDiff < 10) {
        flags.bucket510 = '●';
      } else if (absDiff >= 10 && absDiff < 20) {
        flags.bucket1020 = '●';
      } else if (absDiff >= 20) {
        flags.bucket20 = '●';
      }
      return flags;
    },
    async fetchTmReferenceValues(instrumentIds = []) {
      const project = this.selectedProject;
      if (!project || !project._id || !instrumentIds.length) return {};
      try {
        const tz = this.timezone || 'UTC';
        const targetMoment = this.$moment.tz(this.tmReferenceIso, tz);
        if (!targetMoment || typeof targetMoment.isValid !== 'function' || !targetMoment.isValid()) return {};
        const start = targetMoment.clone().startOf('hour').toISOString();
        const end = targetMoment.clone().add(1, 'hour').toISOString();
        const res = await this.$axios({
          category: 'project',
          method: 'post',
          apiName: `${project._id}/output/instrument-export`,
          data: {
            instrument: instrumentIds,
            type: 'measure',
            interval: 'all',
            date: [start, end],
          },
        });
        const csv = typeof res.data === 'string' ? res.data : '';
        return this.extractValuesAtTimestamp(csv, targetMoment);
      } catch (error) {
        console.error(error);
        return {};
      }
    },
    extractValuesAtTimestamp(csv = '', targetMoment = null) {
      if (!targetMoment || typeof targetMoment.isValid !== 'function' || !targetMoment.isValid()) return {};
      const lines = String(csv || '').split(/\r?\n/).filter(line => line.trim().length);
      if (!lines.length) return {};
      const headers = lines[0].split(',').map(h => this.normalizeHeaderName(h));
      const tz = this.timezone || 'UTC';
      let matchedRow = null;
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',');
        const tsRaw = this.normalizeHeaderName(cols[0] || '');
        if (!tsRaw) continue;
        let tsMoment = this.$moment(tsRaw);
        if (tsMoment && typeof tsMoment.tz === 'function') tsMoment = tsMoment.tz(tz);
        if (!tsMoment || !tsMoment.isValid()) continue;
        if (tsMoment.isSame(targetMoment)) {
          matchedRow = cols;
          break;
        }
      }
      if (!matchedRow) return {};
      const map = {};
      for (let c = 1; c < headers.length && c < matchedRow.length; c++) {
        const headerKey = headers[c];
        if (!headerKey) continue;
        const value = this.normalizeHeaderName(matchedRow[c]);
        map[headerKey] = value === '' ? null : value;
      }
      return map;
    },
    formatTmReferenceValue(value) {
      if (value == null || value === '') return 'NAN';
      const num = Number(value);
      if (Number.isNaN(num)) return 'NAN';
      return num.toFixed(2);
    },
    async run() {
      if (!this.range.length) { this.$message?.warning?.('請先選擇月份'); return; }
      if (!this.rainInstruments.length) { await this.loadStations(); }
      if (!this.elpInstruments.length) { await this.loadElpList(); }
      if (!this.rainInstruments.length) { this.error = '找不到 RAIN 儀器'; return; }
      this.tmLastValidLabel = '';
      this.tmLastValidIso = '';
      this.loading = true;
      this.error = '';
      try {
        const [start, end] = this.range;
        // 取全部 RAIN 儀器，一次呼叫雨量匯出 API（CSV）
        const ids = this.rainInstruments.map(i => i.id);
        const payload = {
          instrument: ids,
          type: 'measure',
          interval: 'daily',
          date: [start, end],
          mode: 'rainExport',
        };
        const res = await this.$axios({
          category: 'project',
          method: 'post',
          apiName: `${this.selectedProject._id}/output/instrument-export`,
          data: payload,
        });
        const csv = typeof res.data === 'string' ? res.data : '';
        this.rawRainCsvData = csv;
        const { sums: totalsByName, maxes: maxesByName } = this.parseRainCsvStats(csv);
        // 對應回儀器，形成點資料
        const pts = this.rainInstruments.map(it => ({
          name: it.name,
          tag: it.mileageTag,
          x: Number(it.mileageValue),
          y: Number(totalsByName[this.normalizeHeaderName(it.name)] || 0),
        }));
        // 僅保留範圍內，並依里程排序
        this.points = pts.filter(p => this.isMileageInRange(p.x)).sort((a, b) => a.x - b.x);
        
        // 2. 【新增】日最大雨量資料處理
        const maxPts = this.rainInstruments.map(it => ({
          name: it.name,
          tag: it.mileageTag,
          x: Number(it.mileageValue),
          y: Number(maxesByName[this.normalizeHeaderName(it.name)] || 0),
        }));
        this.dailyMaxPoints = maxPts.filter(p => this.isMileageInRange(p.x)).sort((a, b) => a.x - b.x);

        // 繪圖
        this.buildChart();
        this.buildDailyMaxChart(); // 【新增】繪製第二張圖

        // 構建 ELP 月度統計表
        const elpIds = this.elpInstruments.map(i => i.id);
        if (elpIds.length) {
          const elpRes = await this.$axios({
            category: 'project',
            method: 'post',
            apiName: `${this.selectedProject._id}/output/instrument-export`,
            data: { instrument: elpIds, type: 'measure', interval: 'all', date: [start, end] },
          });
          const { statsByName, lastDateLabel } = this.parseElpCsv(elpRes.data);

          this.lastLabel = lastDateLabel;
          this.elpRows = this.elpInstruments.map(elp => {
            const key = this.normalizeHeaderName(elp.name);
            const s = statsByName[key] || {};
            const lastTime = new Date(s.lastTs).getTime();
            const rangeTime = new Date(this.range[1]).getTime();

            let isTimeMatch = false;
            if (this.isCurrentMonthLastDay) {
              isTimeMatch = this.$moment(s.lastTs).isSame(this.range[1], 'day');
            } else {
              isTimeMatch = (lastTime === rangeTime);
            }

            // 如果時間不符合，才把數值設為 null
            if (!isTimeMatch) {
              s.last = null;
            }

            const near = this.findNearestRain(elp.mileageValue);
            const rainKey = near ? this.normalizeHeaderName(near.name) : '';
            const rainTotal = totalsByName[rainKey] ?? 0;
            const d = (s.last != null && s.first != null) ? (s.last - s.first) : null;
            let trend = '';
            if (d != null) trend = d > 0 ? '上升' : d < 0 ? '下降' : '無變化';
            return {
              id: elp.id,
              position: elp.name,
              rainTotal: this.fmtNum(rainTotal, 1),
              wlMax: this.fmtNum(s.max, 2),
              wlMin: this.fmtNum(s.min, 2),
              wlLast: this.fmtNum(s.last, 2),
              trend,
              delta: this.fmtSigned(d, 2),
              wlRange: this.fmtNum((s.max - s.min), 2),
            };
          });
        } else {
          this.elpRows = [];
        }
        await this.loadTmTable(true);

        // --- 新增程式碼開始 ---
        // 1. 篩選水位變化量 >= 0.3 或 <= -0.3 的儀器名稱
        const alertInstrumentNames = this.elpRows
          .filter(row => {
            const val = parseFloat(row.delta);
            return !Number.isNaN(val) && Math.abs(val) >= 0.3;
          })
          .map(row => row.position); // 取出儀器名稱 (position 欄位存的是名稱)

        // 2. 轉換時間格式 (this.range 是 ISO String，轉為 Date 物件傳給 autoReportComponent)
        const dateObjRange = this.range.map(d => new Date(d));

        const sensitiveKeywords = ['TK31', 'TK93', 'TK96', 'TK98', 'TK100', 'TK123', 'TK126'];

        // 篩選出敏感邊坡的資料列
        this.sensitiveElpRows = this.elpRows.filter(row => 
          sensitiveKeywords.some(key => row.position.includes(key)),
        );

        // 修改 elpRows 的邏輯，讓符合敏感邊坡的項目帶有一個標記位元
        this.elpRows = this.elpRows.map(row => {
          const isSensitive = sensitiveKeywords.some(key => row.position.includes(key));
          return { ...row, isSensitive };
        });

        // 3. 發送事件給父層
        this.$emit('report-generated', {
          dateRange: dateObjRange,
          names: alertInstrumentNames,
        });
        // --- 新增程式碼結束 ---
      } catch (e) {
        console.error(e);
        this.error = '取得雨量資料失敗';
      } finally {
        this.loading = false;
      }
    },
    // 取得與某里程最近的 RAIN 儀器
    findNearestRain(mileage) {
      if (!this.rainInstruments.length || !Number.isFinite(mileage)) return null;
      let best = null; let dist = Infinity;
      for (const r of this.rainInstruments) {
        const d = Math.abs(r.mileageValue - mileage);
        if (d < dist) { best = r; dist = d; }
      }
      return best;
    },
    // 解析 ELP 匯出 CSV，計算 最高/最低/首筆/末筆
    parseElpCsv(csv = '') {
      const lines = String(csv || '').split(/\r?\n/).filter(l => l.trim().length);
      if (!lines.length) return { statsByName: {}, lastDateLabel: '' };
      const headers = lines[0].split(',').map(h => this.normalizeHeaderName(h));
      const stats = {};
      
      // 新增 lastDateStr (紀錄最後日期) 與 lastDayStartVal (紀錄當日首筆/00:00數值)
      const getS = (name) => (stats[name] = stats[name] || { 
        max: null, 
        min: null, 
        first: null, 
        last: null, 
        lastTs: null, 
        lastDateStr: null, 
        lastDayStartVal: null,
      });
      
      let globalLastTs = '';
      
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',');
        const ts = this.normalizeHeaderName(cols[0] || '');
        if (ts) globalLastTs = ts;
        
        let dateStr = '';
        if (ts) {
          try {
            const m = this.$moment(ts).tz(this.timezone || 'UTC');
            if (m && m.isValid()) {
              dateStr = m.format('YYYY-MM-DD');
            }
          // eslint-disable-next-line no-empty
          } catch (e) {}
        }

        for (let c = 1; c < headers.length && c < cols.length; c++) {
          const key = headers[c];
          const s = getS(key);
          const vStr = this.normalizeHeaderName(cols[c]);
          if (vStr === '') continue;
          const v = parseFloat(vStr);
          if (Number.isNaN(v)) continue;

          if (v > 0 || v < -120) continue;
          
          if (s.first == null) s.first = v;
          
          // 關鍵邏輯：若跨日，則記錄該日的第一筆數值 (作為 00:00 的代表值)
          if (dateStr && s.lastDateStr !== dateStr) {
            s.lastDateStr = dateStr;
            s.lastDayStartVal = v;
          }

          // 這裡保留原本邏輯繼續更新，但在迴圈結束後會覆寫
          s.last = v; 
          s.lastTs = ts;
          s.max = (s.max == null) ? v : Math.max(s.max, v);
          s.min = (s.min == null) ? v : Math.min(s.min, v);
        }
      }
      
      // 迴圈結束，將最後一筆數值 (last) 覆蓋為最後一天的 00:00 數值 (lastDayStartVal)
      Object.values(stats).forEach(s => {
        if (s.lastDayStartVal != null) {
          s.last = s.lastDayStartVal;
        }
      });

      const toLabel = (ts) => {
        try { 
          const d = this.$moment(ts).tz(this.timezone || 'UTC'); 
          // 加上 " 00:00" 讓 UI 顯示及 Word 匯出時的表頭更明確
          return `${d.month() + 1}月${d.date()}日 00:00`; 
        } catch (_) { return ''; }
      };
      
      return { statsByName: stats, lastDateLabel: toLabel(globalLastTs) };
    },
    fmtNum(v, d = 2) { if (v == null || Number.isNaN(v)) return ''; return Number(v).toFixed(d); },
    fmtSigned(v, d = 2) { if (v == null || Number.isNaN(v)) return ''; const n = Number(v); return (n >= 0 ? '' : '-') + Math.abs(n).toFixed(d); },
    normalizeHeaderName(s = '') {
      return String(s || '')
        .replace(/^\uFEFF/, '') // 移除 BOM
        .replace(/[\r\n]/g, '')
        .replace(/"/g, '"')
        .replace(/"/g, '')
        .trim();
    },
    parseAndSumCsv(csv = '') {
      // CSV 第一列為標題: timestamp, instA, instB, ...
      const lines = csv.split(/\r?\n/).filter(l => l.trim().length);
      if (!lines.length) return {};
      const headers = lines[0].split(',').map(h => this.normalizeHeaderName(h));
      const sums = new Array(headers.length).fill(0);
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',');
        for (let c = 1; c < headers.length && c < cols.length; c++) {
          const clean = this.normalizeHeaderName(cols[c]);
          const v = parseFloat(clean);
          if (!Number.isNaN(v)) sums[c] += v;
        }
      }
      const map = {};
      for (let i = 1; i < headers.length; i++) map[headers[i]] = sums[i];
      return map;
    },
    parseRainCsvStats(csv = '') {
      const lines = csv.split(/\r?\n/).filter(l => l.trim().length);
      if (!lines.length) return { sums: {}, maxes: {} };
      
      const headers = lines[0].split(',').map(h => this.normalizeHeaderName(h));
      const sums = new Array(headers.length).fill(0);
      const maxes = new Array(headers.length).fill(0); // 初始化最大值陣列

      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',');
        for (let c = 1; c < headers.length && c < cols.length; c++) {
          const clean = this.normalizeHeaderName(cols[c]);
          const v = parseFloat(clean);
          if (!Number.isNaN(v)) {
            sums[c] += v;                  // 累加
            if (v > maxes[c]) maxes[c] = v; // 【關鍵】更新最大值
          }
        }
      }
      
      const sumMap = {};
      const maxMap = {};
      for (let i = 1; i < headers.length; i++) {
        sumMap[headers[i]] = sums[i];
        maxMap[headers[i]] = maxes[i];
      }
      return { sums: sumMap, maxes: maxMap };
    },
    exportTmExcel() {
      if (!this.$refs.tmTable) return;
      // 直接將 HTML table 轉化為 Excel 工作表，它會自動處理合併儲存格
      const wb = XLSX.utils.table_to_book(this.$refs.tmTable, { sheet: '地滑計變化比較' });
      const filename = `各自動監測站地滑計數值變化比較_${this.$moment().tz(this.timezone || 'UTC').format('YYYYMMDD_HHmmss')}.xlsx`;
      XLSX.writeFile(wb, filename);
    },
    async exportDocx() {
      if (this.exporting) return;
      if (!this.chartOptions) {
        this.$message?.warning?.('請先產生圖表資料');
        return;
      }

      this.exporting = true;
      try {
        // 1. 準備第一張圖 (月累積雨量)
        const chartImage = await this.prepareChartImage('reportChart');
        if (!chartImage) {
          this.$message?.error?.('無法取得圖表快照');
          return;
        }

        // 2. 準備第二張圖 (日最大雨量)
        const dailyChartImage = await this.prepareChartImage('reportChartDaily');

        const children = [];
        let monthDisplay = '';
        if (this.selectedMonth) {
          const parsedMonth = this.$moment(this.selectedMonth, 'YYYY-MM', true);
          if (parsedMonth.isValid()) {
            monthDisplay = parsedMonth.format('YYYY/MM');
          }
        }
        const titleText = (monthDisplay ? `Auto Monthly Report ${monthDisplay}` : 'Auto Monthly Report').trim();

        children.push(
          new Paragraph({
            children: [new TextRun({ text: titleText, bold: true, size: 32 })],
            heading: HeadingLevel.TITLE,
            alignment: AlignmentType.CENTER,
            spacing: { before: 200, after: 200 },
          }),
        );

        if (this.rangeLabel) {
          children.push(
            new Paragraph({
              text: `時間範圍: ${this.rangeLabel}`,
              alignment: AlignmentType.CENTER,
            }),
          );
        }

        // 圖表 1 區塊
        children.push(this.createDocxHeading('自動監測站雨量變化'));
        children.push(
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new ImageRun({
                data: chartImage.bytes,
                transformation: { width: chartImage.width, height: chartImage.height },
              }),
            ],
          }),
        );
        
        // 圖表 2 區塊 (日最大雨量)
        if (dailyChartImage) {
          children.push(new Paragraph({ children: [], spacing: { before: 200 } })); // 間隔
          children.push(this.createDocxHeading('自動監測站日雨量變化'));
          children.push(
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [
                new ImageRun({
                  data: dailyChartImage.bytes,
                  transformation: { width: dailyChartImage.width, height: dailyChartImage.height },
                }),
              ],
            }),
          );
        }

        const elpRows = Array.isArray(this.elpRows) ? this.elpRows : [];
        children.push(new Paragraph({ children: [], pageBreakBefore: true }));
        // ======== 新增欄位說明文字 ========
        children.push(
          new Paragraph({
            spacing: { after: 200 },
            children: [
              new TextRun({
                text: '月累積雨量(mm): 獲取距離該儀器最近之雨量計當月1號至隔月1號00:00所有數據總和。',
                color: 'FF0000',
                size: 24,
                font: { ascii: 'Times New Roman', eastAsia: 'DFKai-SB', hAnsi: 'Times New Roman' },
              }),
              new TextRun({
                text: '水位高程(GL.): 最高/最低為時間範圍數據內尋找小於0且大於-120之數值。',
                color: 'FF0000',
                size: 24,
                break: 1,
                font: { ascii: 'Times New Roman', eastAsia: 'DFKai-SB', hAnsi: 'Times New Roman' },
              }),
              new TextRun({
                text: '月水位變化(m): 本月水位變化量(m)為隔月1號00:00數據，減去當月第一筆數據。',
                color: 'FF0000',
                size: 24,
                break: 1,
                font: { ascii: 'Times New Roman', eastAsia: 'DFKai-SB', hAnsi: 'Times New Roman' },
              }),
              new TextRun({
                text: '本月最大水位變動量(m): 計算方式=最高-最低。',
                color: 'FF0000',
                size: 24,
                break: 1,
                font: { ascii: 'Times New Roman', eastAsia: 'DFKai-SB', hAnsi: 'Times New Roman' },
              }),
              new TextRun({
                text: '表格樣式: 黃底代表該欄為敏感邊坡之儀器；紅色字體代表數值為負。',
                color: 'FF0000',
                size: 24,
                break: 1,
                font: { ascii: 'Times New Roman', eastAsia: 'DFKai-SB', hAnsi: 'Times New Roman' },
              }),
              new TextRun({
                text: '※表格空白表示無有效數據。',
                color: 'FF0000',
                size: 24,
                break: 1,
                font: { ascii: 'Times New Roman', eastAsia: 'DFKai-SB', hAnsi: 'Times New Roman' },
              }),
            ],
          }),
        );
        // ==================================
        children.push(this.createDocxHeading('水壓計水位變化彙整表'));
        children.push(this.buildElpDocxTable(elpRows));

        children.push(new Paragraph({ children: [], pageBreakBefore: true }));
        children.push(this.createDocxHeading('敏感邊坡水位變化'));
        children.push(this.buildElpDocxTable(this.sensitiveElpRows)); // 重用表格組件

        const elpAlertRows = elpRows.filter(row => {
          const val = parseFloat(row.delta); 
          return !Number.isNaN(val) && Math.abs(val) >= 0.3;
        });

        if (elpAlertRows.length > 0) {
          children.push(new Paragraph({ children: [], spacing: { before: 400 } })); 
          children.push(this.createDocxHeading('本月水位變化量達(≥ ±0.3m)儀器列表'));
          children.push(this.buildElpDocxTable(elpAlertRows));
        }

        children.push(new Paragraph({ children: [], pageBreakBefore: true }));
        // ======== 新增欄位說明文字 ========
        children.push(
          new Paragraph({
            spacing: { after: 200 },
            children: [
              new TextRun({
                text: '地滑計讀值(mm): 第一欄為初始值參照日期之讀值；第二欄為本月1號00:00之讀值；第三欄為隔月1號00:00之讀值。',
                color: 'FF0000',
                size: 24,
                font: { ascii: 'Times New Roman', eastAsia: 'DFKai-SB', hAnsi: 'Times New Roman' },
              }),
              new TextRun({
                text: '總計變位量(mm): 第一欄計算公式=上個月-初始值；第二欄為計算公式=本月-初始值；第三欄為本月-上個月。',
                color: 'FF0000',
                size: 24,
                break: 1,
                font: { ascii: 'Times New Roman', eastAsia: 'DFKai-SB', hAnsi: 'Times New Roman' },
              }),
            ],
          }),
        );
        // ==================================
        children.push(this.createDocxHeading('各自動監測站地滑計數值變化比較'));

        children.push(this.buildTmDocxTable());

        children.push(new Paragraph({ children: [], pageBreakBefore: true }));
        children.push(this.createDocxHeading('地滑計變位量統計表'));
        children.push(this.buildTmSummaryDocxTable());

        const doc = new Document({
          styles: {
            default: {
              document: {
                run: {
                  font: {
                    ascii: 'Times New Roman',
                    hAnsi: 'Times New Roman',
                    eastAsia: 'DFKai-SB',
                  },
                  size: 32,
                },
              },
            },
          },
          properties: {
            title: titleText,
            creator: 'AutoReportMonthly',
            description: 'Auto monthly report export',
          },
          sections: [
            {
              properties: {
                page: {
                  size: { width: 23811, height: 16840},
                },
              },
              children,
            },
          ],
        });

        const blob = await Packer.toBlob(doc);
        const filename = `auto-monthly_${this.$moment()
          .tz(this.timezone || 'UTC')
          .format('YYYYMMDD_HHmmss')}.docx`;

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);

        setTimeout(() => {
          this.exportTmExcel();
        }, 200);

        this.$message?.success?.('已匯出 .docx');
      } catch (error) {
        console.error('Failed to export docx', error);
        this.$message?.error?.('匯出失敗，請稍後再試');
      } finally {
        this.exporting = false;
      }
    },
    createDocxHeading(text) {
      return new Paragraph({
        children: [
          new TextRun({
            text,
            bold: true,
            font: 'DFKai-SB',
            size: 28,
          }),
        ],
        spacing: { before: 400, after: 200 },
        alignment: AlignmentType.LEFT,
      });
    },
    buildDocxTable(headers, rows) {
      const headerShading = { type: 'clear', color: 'auto', fill: '1ABC9C' };
      const headerRow = new TableRow({
        children: headers.map(header =>
          this.createDocxTableCell(header, {
            bold: true,
            fontSize: 24,
            color: 'FFFFFF',
            shading: headerShading,
          }),
        ),
      });

      const dataRows = rows.map(cols =>
        new TableRow({
          children: cols.map(value => this.createDocxTableCell(value)),
        }),
      );

      return new Table({
        rows: [headerRow, ...dataRows],
        width: { size: 100, type: WidthType.PERCENTAGE },
        alignment: AlignmentType.CENTER, // 整張表水平置中
      });
    },
    buildElpDocxTable(rowsData) {
      const headerShading = { type: 'clear', color: 'auto', fill: '1ABC9C' };
      const monthLabel = this.monthLabel || '';

      // ----------- 修改開始 -----------
      // 1. 修改雨量標題：加入 \n 進行換行
      const rainfallLabel = monthLabel 
        ? `${monthLabel}\n累積雨量\n(mm)` 
        : '累積雨量\n(mm)';
      
      const trendTitle = monthLabel ? `${monthLabel}水位變化(m)` : '水位變化(m)';
      const lastWaterLabel = this.lastLabel || '最後';
      // ----------- 修改結束 -----------

      const rows = [
        new TableRow({
          tableHeader: true,
          children: [
            this.createDocxTableCell('儀器位置', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, rowSpan: 2 }),
            this.createDocxTableCell(rainfallLabel, { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, rowSpan: 2 }),
            this.createDocxTableCell('水位高程(GL.)', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, columnSpan: 3 }),
            this.createDocxTableCell(trendTitle, { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, columnSpan: 2 }),
            this.createDocxTableCell('本月最大水位變動量(m)', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, rowSpan: 2 }),
          ],
        }),
        new TableRow({
          tableHeader: true,
          children: [
            this.createDocxTableCell('最高', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell('最低', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell(lastWaterLabel, { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell('升降', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            // ----------- 修改開始 -----------
            // 2. 修改本月水位變化量：直接在字串中加入 \n
            this.createDocxTableCell('本月水位\n變化量(m)', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            // ----------- 修改結束 -----------
          ],
        }),
      ];

      if (!Array.isArray(rowsData) || !rowsData.length) {
        rows.push(
          new TableRow({
            children: [
              this.createDocxTableCell('無資料', { columnSpan: 7 }),
            ],
          }),
        );
        return new Table({
          rows,
          width: { size: 100, type: WidthType.PERCENTAGE },
        });
      }

      rowsData.forEach(row => {
        const rowShading = row.isSensitive ? { type: 'clear', color: 'auto', fill: 'FFFF00' } : null;
        rows.push(
          new TableRow({
            children: [
              // 傳入 shading 參數給 Cell
              this.createDocxTableCell(row.position || '', { shading: rowShading }),
              this.createDocxTableCell(row.rainTotal ?? '', { shading: rowShading }),
              this.createDocxTableCellWithColor(row.wlMax ?? '', rowShading), // 需同步修改此 method
              this.createDocxTableCellWithColor(row.wlMin ?? '', rowShading),
              this.createDocxTableCellWithColor(row.wlLast ?? '', rowShading),
              this.createDocxTableCell(row.trend || '', { shading: rowShading }),
              this.createDocxTableCellWithColor(row.delta ?? '', rowShading),
              this.createDocxTableCellWithColor(row.wlRange ?? '', rowShading),
            ],
          }),
        );
      });

      return new Table({
        rows,
        width: { size: 100, type: WidthType.PERCENTAGE },
      });
    },
    createDocxTableCellWithColor(value, shading = null) {
      const text = value ?? '';
      const num = parseFloat(text);
      const isNumber = !isNaN(num);
      const color = isNumber && num < 0 ? 'FF0000' : '000000';

      return new TableCell({
        shading,
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: String(text),
                color,
                font: { ascii: 'Times New Roman', eastAsia: 'DFKai-SB', hAnsi: 'Times New Roman' },
                size: 24,
              }),
            ],
            alignment: AlignmentType.CENTER,
          }),
        ],
        verticalAlign: VerticalAlign.CENTER,
      });
    },
    createDocxTableCell(text, { bold = false, fontSize = 24, color, shading, alignment = AlignmentType.CENTER, columnSpan, rowSpan } = {}) {
      const runs = this.createDocxTextRuns(text, { bold, fontSize, color });
      const paragraph = new Paragraph({ children: runs, alignment });
      const options = { 
        children: [paragraph],
        verticalAlign: VerticalAlign.CENTER, 
      };
      if (shading) options.shading = shading;
      if (columnSpan && columnSpan > 1) options.columnSpan = columnSpan;
      if (rowSpan && rowSpan > 1) options.rowSpan = rowSpan;
      return new TableCell(options);
    },
    createDocxTextRuns(text, { bold = false, fontSize = 24, color } = {}) {
      // const font = 'DFKai-SB';
      const value = text == null ? '' : String(text);
      const normalized = value.replace(/<br\s*\/?>/gi, '\n').split(/\n/);
      const runs = [];
      normalized.forEach((segment, index) => {
        if (index > 0) runs.push(new TextRun({ text: '', break: 1 }));
        runs.push(
          new TextRun({
            text: segment.replace(/<[^>]+>/g, ''),
            bold,
            font: {
              ascii: 'Times New Roman',     // 英文與符號
              eastAsia: 'DFKai-SB',         // 中文
              hAnsi: 'Times New Roman',     // 西文替代
            },
            size: fontSize,
            color,
          }),
        );
      });
      if (!runs.length) {
        runs.push(new TextRun({ 
          text: '', 
          bold, 
          font: {
            ascii: 'Times New Roman',     // 英文與符號
            eastAsia: 'DFKai-SB',         // 中文
            hAnsi: 'Times New Roman',     // 西文替代
          }, 
          size: fontSize, 
          color }));
      }
      return runs;
    },
    buildTmDocxTable() {
      const headerShading = { type: 'clear', color: 'auto', fill: '1ABC9C' };
      const rows = [];

      rows.push(
        new TableRow({
          tableHeader: true,
          children: [
            this.createDocxTableCell('項次', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, rowSpan: 3 }),
            this.createDocxTableCell('標別', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, rowSpan: 3 }),
            this.createDocxTableCell('自動站里程', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, rowSpan: 3 }),
            this.createDocxTableCell('儀器編號', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, rowSpan: 3 }),
            this.createDocxTableCell(`${this.tmReferenceLabelfull} ~ ${this.tmLastValidLabel || this.tmCurrLabel}`, { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, columnSpan: 8 }),
            this.createDocxTableCell('備註', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, columnSpan: 7 }),
          ],
        }),
      );

      rows.push(
        new TableRow({
          tableHeader: true,
          children: [
            this.createDocxTableCell('地滑計讀值(mm)', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, columnSpan: 3 }),
            this.createDocxTableCell('管理值檢核', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, columnSpan: 2 }),
            this.createDocxTableCell('總計變位量(mm)', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, columnSpan: 3 }),
            this.createDocxTableCell(`${this.selectedYear}年1月迄今~總計變位量達`, { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, columnSpan: 3 }),
            this.createDocxTableCell('本月總計變位量達', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, columnSpan: 3 }),
            this.createDocxTableCell('註記', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, rowSpan: 2 }),
          ],
        }),
      );

      rows.push(
        new TableRow({
          tableHeader: true,
          children: [
            this.createDocxTableCell(this.tmReferenceLabel, { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell(this.tmPrevLabel, { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell(this.tmCurrLabel, { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell('注意值<br>(±30mm)', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell('行動值<br>(>10mm/hr)', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell(this.tmPrevLabel, { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell(this.tmCurrLabel, { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell('本月變化', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell('5~10mm', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell('10~20mm', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell('>20mm', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell('5~10mm', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell('10~20mm', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell('>20mm', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
          ],
        }),
      );

      const totalColumns = 19;
      const dataRows = [];

      if (!Array.isArray(this.tmTableRows) || !this.tmTableRows.length) {
        dataRows.push(
          new TableRow({
            children: [
              this.createDocxTableCell('????', { columnSpan: totalColumns }),
            ],
          }),
        );
      } else {
        let stationCounter = 0; // 站點計數器
        let labelSpanRemaining = 0;
        let stationSpanRemaining = 0;
        this.tmTableRows.forEach(row => {
          const cells = [];
          // 1. 處理項次與站點里程的合併 (兩者通常是同步的)
          if (stationSpanRemaining <= 0) {
            stationCounter++;
            const span = Math.max(row.stationRowSpan || 1, 1);
            
            // 新增項次 Cell，並設定合併行數
            cells.push(this.createDocxTableCell(String(stationCounter), { rowSpan: span }));
            
            // 標別 Cell (如果需要跟標別同步也可以寫在這裡，但原本邏輯有 labelSpanRemaining 分開處理)
            // 這裡為了保持原邏輯穩定，我們只控制「項次」與「站點」同步
          }

          // 2. 標別 (依據 labelRowSpan 合併)
          if (labelSpanRemaining <= 0) {
            const span = Math.max(row.labelRowSpan || 1, 1);
            cells.push(this.createDocxTableCell(this.labelMap(row.label) || '', { rowSpan: span }));
            labelSpanRemaining = span - 1;
          } else {
            labelSpanRemaining -= 1;
          }

          // 3. 站點里程 (依據 stationRowSpan 合併)
          if (stationSpanRemaining <= 0) {
            const span = Math.max(row.stationRowSpan || 1, 1);
            cells.push(this.createDocxTableCell(row.stationName || '-', { rowSpan: span }));
            stationSpanRemaining = span - 1;
          } else {
            stationSpanRemaining -= 1;
          }
          cells.push(this.createDocxTableCell(row.instrumentName || '-'));
          cells.push(this.createDocxTableCell(row.referenceValue ?? ''));
          cells.push(this.createDocxTableCell(row.prevSnapshotDisplay ?? ''));
          cells.push(this.createDocxTableCell(row.currSnapshotDisplay ?? ''));
          cells.push(this.createDocxTableCell(row.alert30 ?? ''));
          cells.push(this.createDocxTableCell(''));
          cells.push(this.createDocxTableCell(row.prevSnapshotDiff ?? ''));
          cells.push(this.createDocxTableCell(row.currSnapshotDiff ?? ''));
          cells.push(this.createDocxTableCell(row.currVsPrevDiff ?? ''));
          cells.push(this.createDocxTableCell(row.bucket510 ?? ''));
          cells.push(this.createDocxTableCell(row.bucket1020 ?? ''));
          cells.push(this.createDocxTableCell(row.bucket20 ?? ''));
          cells.push(this.createDocxTableCell(row.monthBucket510 ?? ''));
          cells.push(this.createDocxTableCell(row.monthBucket1020 ?? ''));
          cells.push(this.createDocxTableCell(row.monthBucket20 ?? ''));
          cells.push(this.createDocxTableCell(row.remark ?? ''));

          dataRows.push(new TableRow({ children: cells }));
        });
      }

      return new Table({
        rows: [...rows, ...dataRows],
        width: { size: 100, type: WidthType.PERCENTAGE },
      });
    },
    ensureRows(rows, colCount) {
      if (Array.isArray(rows) && rows.length > 0) return rows;
      const placeholder = new Array(Math.max(colCount, 1)).fill('');
      placeholder[0] = '無資料';
      return [placeholder];
    },
    // 【修改】支援傳入 refName
    async prepareChartImage(refName = 'reportChart') {
      const dataUrl = await this.getChartDataUrl(refName);
      if (!dataUrl) return null;
      const { width, height, bytes } = await this.addBorderAndGetBytes(dataUrl, 4, '#000');
      const MAX_WIDTH = 1200;
      const scale = width > MAX_WIDTH ? MAX_WIDTH / width : 1;
      return {
        bytes,
        width: Math.round(width * scale),
        height: Math.round(height * scale),
      };
    },
    // 【修改】支援傳入 refName
    async getChartDataUrl(refName) {
      await this.$nextTick();
      const instance = this.getChartInstance(refName);
      if (!instance || typeof instance.getDataURL !== 'function') return '';
      try {
        return instance.getDataURL({ type: 'png', backgroundColor: '#fff', pixelRatio: 2 });
      } catch (error) {
        console.error('Failed to capture chart data URL', error);
        return '';
      }
    },
    // 【修改】支援傳入 refName
    getChartInstance(refName) {
      const comp = this.$refs[refName];
      if (!comp) return null;
      if (typeof comp.getEchartsInstance === 'function') {
        const inst = comp.getEchartsInstance();
        if (inst) return inst;
      }
      if (comp.echarts) return comp.echarts;
      if (comp.chart) return comp.chart;
      if (comp._chart) return comp._chart;
      if (comp.$refs) {
        const nested = Object.values(comp.$refs);
        for (const ref of nested) {
          if (!ref) continue;
          if (typeof ref.getEchartsInstance === 'function') {
            const inst = ref.getEchartsInstance();
            if (inst) return inst;
          }
          if (ref.echarts) return ref.echarts;
          if (ref.chart) return ref.chart;
        }
      }
      if (Array.isArray(comp.$children)) {
        for (const child of comp.$children) {
          if (!child) continue;
          if (typeof child.getEchartsInstance === 'function') {
            const inst = child.getEchartsInstance();
            if (inst) return inst;
          }
          if (child.echarts) return child.echarts;
          if (child.chart) return child.chart;
        }
      }
      return null;
    },
    dataURLToUint8Array(dataURL) {
      const parts = dataURL.split(',');
      if (parts.length < 2) throw new Error('Invalid image dataURL');
      const base64 = parts[1];
      const binary = atob(base64);
      const len = binary.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      return bytes;
    },
    getImageSize(dataURL) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = () => reject(new Error('載入圖像失敗'));
        img.src = dataURL;
      });
    },
    addBlackBorderToDataURL(dataURL, border = 4, color = '#000') {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const width = img.naturalWidth + border * 2;
            const height = img.naturalHeight + border * 2;
            canvas.width = width;
            canvas.height = height;
            ctx.fillStyle = color;
            ctx.fillRect(0, 0, width, height);
            ctx.drawImage(img, border, border);
            const output = canvas.toDataURL('image/png');
            resolve(output);
          } catch (err) {
            reject(err);
          }
        };
        img.onerror = () => reject(new Error('載入圖像失敗'));
        img.src = dataURL;
      });
    },
    async addBorderAndGetBytes(dataURL, border = 4, color = '#000') {
      const withBorder = await this.addBlackBorderToDataURL(dataURL, border, color);
      const { width, height } = await this.getImageSize(withBorder);
      const bytes = this.dataURLToUint8Array(withBorder);
      return { withBorder, width, height, bytes };
    },
    buildChart() {
      const title = '自動監測站雨量變化';

      // 原始資料
      const dataPoints = this.points.map(p => ({
        value: [p.x, p.y],
        name: p.tag || p.name,
      }));

      // 🔹 取雨量最高前三名（依 y 值排序）
      const top3 = [...this.points]
        .sort((a, b) => b.y - a.y)
        .slice(0, 3)
        .map((p, i) => ({
          name: `${i + 1}. ${p.tag || p.name}`,
          value: [p.x, p.y],
          label: {
            show: true,
            lineStyle: { color: '#666', width: 1, type: 'dashed' },
            length: 6,
            position: 'top',
            distance: 6,
            formatter: `${p.tag || p.name}\n${p.y.toFixed(1)} mm`,
            fontWeight: 'bold',
            fontSize: 12,
            backgroundColor: 'rgba(255,255,200,0.9)',
            borderColor: '#666',
            borderWidth: 1,
            borderRadius: 4,
            padding: [4, 6],
          },
          itemStyle: {
            color: ['#ff3b30', '#ff9500', '#ffcc00'][i], // 紅 / 橘 / 黃
            borderColor: '#000',
            borderWidth: 2,
          },
          symbolSize: 14,
          emphasis: { scale: true },
        }));

      const stations = [
        { name: '台北', mileage: 4.803 },
        { name: '板橋', mileage: 11.297 },
        { name: '桃園', mileage: 40.197 },
        { name: '新竹', mileage: 69.375 },
        { name: '苗栗', mileage: 102.615 },
        { name: '台中', mileage: 162.317 },
        { name: '彰化', mileage: 191.966 },
      ];

      // 🔹 組成圖表設定
      this.chartOptions = {
        backgroundColor: '#fff',
        title: { text: title, left: 'center', textStyle: { fontSize: 18 } },
        tooltip: {
          trigger: 'item',
          formatter: params => {
            const [x, y] = params.value || [];
            return `${params.name}<br/>里程: TK${x.toFixed(3).replace('.', '+')}<br/>月累積雨量: ${y.toFixed(1)} mm`;
          },
        },
        grid: { left: 60, right: 20, top: 60, bottom: 60 },
        xAxis: {
          type: 'value',
          name: '高鐵里程',
          nameLocation: 'middle',
          nameGap: 35,
          min: 0,
          max: 200,
          splitNumber: 10,
          axisLabel: {
            formatter: v => `TK${Math.floor(v)}+${String(Math.round((v % 1) * 1000)).padStart(3, '0')}`,
          },
          splitLine: { show: true },
        },
        yAxis: {
          type: 'value',
          name: '月累積雨量(mm)',
          min: 0,
          // ======== 動態調整 Y 軸上限 ========
          max(value) {
            // value.max 是圖表中實際存在的最大數據
            // 如果最大數據不到 200，強制將 Y 軸上限撐開到 200
            // 如果超過 200，則往上取 50 的整數倍（例如 230 -> 250），讓圖表頂部留有呼吸空間
            return value.max < 200 ? 200 : Math.ceil(value.max / 50) * 50;
          },
          // ===================================
          splitLine: { show: true },
        },
        series: [
          {
            name: '雨量線',
            type: 'line',
            smooth: false,
            showSymbol: true,
            symbolSize: 8,
            lineStyle: { color: '#b0433f', width: 2 },
            itemStyle: { color: '#b0433f' },
            data: this.points.map(p => [p.x, p.y]),
          },
          {
            type: 'scatter',
            name: '全部站點',
            data: dataPoints,
            symbolSize: 10,
            itemStyle: { color: '#b0433f', borderColor: '#7d2a28' },
          },
          {
            // 🔹 顯示前三名雨量最高點
            name: 'Top 3 雨量',
            type: 'scatter',
            data: top3,
            symbol: 'diamond',
            z: 5,
          },
          {
            name: '高鐵站點',
            type: 'scatter',
            symbol: 'circle',
            symbolSize: 10,
            data: stations.map(s => ({
              name: s.name,
              value: [s.mileage, 0],
            })),
            label: {
              show: true,
              position: 'top',
              fontWeight: 'bold',
              color: '#222',
              formatter: '{b}',
            },
            itemStyle: { color: 'yellow', borderColor: '#666', borderWidth: 1 },
            tooltip: { show: false },
            emphasis: { scale: false, disabled: true },
            silent: true,
          },
        ],
      };
    },
    buildDailyMaxChart() {
      const title = '自動監測站日雨量變化';

      // 原始資料
      const dataPoints = this.dailyMaxPoints.map(p => ({
        value: [p.x, p.y],
        name: p.tag || p.name,
      }));

      // 🔹 取雨量最高前三名（依 y 值排序）
      const top3 = [...this.dailyMaxPoints]
        .sort((a, b) => b.y - a.y)
        .slice(0, 3)
        .map((p, i) => ({
          name: `${i + 1}. ${p.tag || p.name}`,
          value: [p.x, p.y],
          label: {
            show: true,
            lineStyle: { color: '#666', width: 1, type: 'dashed' },
            length: 6,
            position: 'top',
            distance: 6,
            formatter: `${p.tag || p.name}\n${p.y.toFixed(1)} mm`,
            fontWeight: 'bold',
            fontSize: 12,
            backgroundColor: 'rgba(255,255,200,0.9)',
            borderColor: '#666',
            borderWidth: 1,
            borderRadius: 4,
            padding: [4, 6],
          },
          itemStyle: {
            color: ['#ff3b30', '#ff9500', '#ffcc00'][i], // 紅 / 橘 / 黃
            borderColor: '#000',
            borderWidth: 2,
          },
          symbolSize: 14,
          emphasis: { scale: true },
        }));

      const stations = [
        { name: '台北', mileage: 4.803 },
        { name: '板橋', mileage: 11.297 },
        { name: '桃園', mileage: 40.197 },
        { name: '新竹', mileage: 69.375 },
        { name: '苗栗', mileage: 102.615 },
        { name: '台中', mileage: 162.317 },
        { name: '彰化', mileage: 191.966 },
      ];

      // 🔹 組成圖表設定
      this.chartOptionsDailyMax = {
        backgroundColor: '#fff',
        title: { text: title, left: 'center', textStyle: { fontSize: 18 } },
        tooltip: {
          trigger: 'item',
          formatter: params => {
            const [x, y] = params.value || [];
            return `${params.name}<br/>里程: TK${x.toFixed(3).replace('.', '+')}<br/>日最大雨量: ${y.toFixed(1)} mm`;
          },
        },
        grid: { left: 60, right: 20, top: 60, bottom: 60 },
        xAxis: {
          type: 'value',
          name: '高鐵里程',
          nameLocation: 'middle',
          nameGap: 35,
          min: 0,
          max: 200,
          splitNumber: 10,
          axisLabel: {
            formatter: v => `TK${Math.floor(v)}+${String(Math.round((v % 1) * 1000)).padStart(3, '0')}`,
          },
          splitLine: { show: true },
        },
        yAxis: {
          type: 'value',
          name: '日最大雨量(mm)',
          min: 0,
          // ======== 動態調整 Y 軸上限 ========
          max(value) {
            // value.max 是圖表中實際存在的最大數據
            // 如果最大數據不到 200，強制將 Y 軸上限撐開到 200
            // 如果超過 200，則往上取 50 的整數倍（例如 230 -> 250），讓圖表頂部留有呼吸空間
            return value.max < 200 ? 200 : Math.ceil(value.max / 50) * 50;
          },
          // ===================================
          splitLine: { show: true },
        },
        series: [
          {
            name: '雨量線',
            type: 'line',
            smooth: false,
            showSymbol: true,
            symbolSize: 8,
            lineStyle: { color: '#b0433f', width: 2 },
            itemStyle: { color: '#b0433f' },
            data: this.dailyMaxPoints.map(p => [p.x, p.y]),
            // ======== 新增警示線 ========
            markLine: {
              symbol: ['none', 'none'], // 不顯示箭頭
              label: { show: true, position: 'insideEndTop', formatter: '{b}' },
              data: [
                {
                  yAxis: 80,
                  name: '大雨 (80mm/d)',
                  lineStyle: { color: '#ff9800', type: 'dashed', width: 2 }, // 橘色虛線
                  label: { color: '#ff9800', fontWeight: 'bold' },
                },
                {
                  yAxis: 200,
                  name: '豪雨 (200mm/d)',
                  lineStyle: { color: '#f44336', type: 'dashed', width: 2 }, // 紅色虛線
                  label: { color: '#f44336', fontWeight: 'bold' },
                },
              ],
            },
            // ============================
          },
          {
            type: 'scatter',
            name: '全部站點',
            data: dataPoints,
            symbolSize: 10,
            itemStyle: { color: '#b0433f', borderColor: '#7d2a28' },
          },
          {
            // 🔹 顯示前三名雨量最高點
            name: 'Top 3 雨量',
            type: 'scatter',
            data: top3,
            symbol: 'diamond',
            z: 5,
          },
          {
            name: '高鐵站點',
            type: 'scatter',
            symbol: 'circle',
            symbolSize: 10,
            data: stations.map(s => ({
              name: s.name,
              value: [s.mileage, 0],
            })),
            label: {
              show: true,
              position: 'top',
              fontWeight: 'bold',
              color: '#222',
              formatter: '{b}',
            },
            itemStyle: { color: 'yellow', borderColor: '#666', borderWidth: 1 },
            tooltip: { show: false },
            emphasis: { scale: false, disabled: true },
            silent: true,
          },
        ],
      };
    },
    buildTmSummaryDocxTable() {
      const headerShading = { type: 'clear', color: 'auto', fill: '1ABC9C' };
      const rows = [];

      // 第一層表頭
      rows.push(
        new TableRow({
          children: [
            this.createDocxTableCell('項目', {
              bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, rowSpan: 2,
            }),
            this.createDocxTableCell(
              `${this.tmReferenceLabelfull} ~ ${this.tmLastValidLabel || this.tmCurrLabel}\n總計變位量達`,
              { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, columnSpan: 3 },
            ),
            this.createDocxTableCell(
              `${this.tmLastValidLabel || this.tmCurrLabel} 單月總計變位量達\n(與 ${this.tmPrevLabel} 相比)`,
              { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading, columnSpan: 3 },
            ),
          ],
        }),
      );

      // 第二層表頭
      rows.push(
        new TableRow({
          children: [
            this.createDocxTableCell('5~10mm', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell('10~20mm', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell('>20mm', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell('5~10mm', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell('10~20mm', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
            this.createDocxTableCell('>20mm', { bold: true, fontSize: 24, color: 'FFFFFF', shading: headerShading }),
          ],
        }),
      );

      // 資料列
      rows.push(
        new TableRow({
          children: [
            this.createDocxTableCell('地滑計數量'),
            this.createDocxTableCell(this.tmSummaryStats.total510 ?? ''),
            this.createDocxTableCell(this.tmSummaryStats.total1020 ?? ''),
            this.createDocxTableCell(this.tmSummaryStats.total20 ?? ''),
            this.createDocxTableCell(this.tmSummaryStats.month510 ?? ''),
            this.createDocxTableCell(this.tmSummaryStats.month1020 ?? ''),
            this.createDocxTableCell(this.tmSummaryStats.month20 ?? ''),
          ],
        }),
      );

      return new Table({
        rows,
        width: { size: 100, type: WidthType.PERCENTAGE },
      });
    },
  },
};
</script>

<style scoped>
.auto-month-report { max-width: 100%; }
.controls { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }
.left { display: flex; align-items: center; gap: 10px; }
.right { display: flex; align-items: center; gap: 10px; }
.range-label { font-size: 13px; color: #666; }
.chart-placeholder { height: 420px; display: flex; align-items: center; justify-content: center; color: #909399; border: 1px dashed #d9d9d9; border-radius: 4px; background: #fafafa; }
.mb-12 { margin-bottom: 12px; }
.chart-card { width: 100%; }
.elp-table .rise, .elp-table .fall { background: #ffd84d; color: #000; font-weight: 600; padding: 2px 6px; border-radius: 2px; }
.elp-table .num { color: #b30000; font-weight: 600; }
.num.negative {
  color: #b30000; /* 負值紅色 */
  font-weight: 600;
}
.num.positive {
  color: #000; /* 正值黑色 */
  font-weight: 600;
}

.tm-table { margin-top: 20px; }
.tm-table-title { font-weight: 600; font-size: 15px; margin: 16px 0 8px; }
.tm-summary-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.tm-summary-table th,
.tm-summary-table td { border: 1px solid #dcdfe6; padding: 6px 10px; }
.tm-summary-table th { background: #f3f6fb; text-align: center; }
.tm-summary-table td { background: #fff; }
.tm-summary-table .empty { text-align: center; color: #909399; font-style: italic; }

:deep(.el-table th),
:deep(.el-table td) {
  text-align: center !important;
  vertical-align: middle !important;
}

.tm-summary-table th,
.tm-summary-table td {
  text-align: center;
  vertical-align: middle;
}

.tm-summary-stats {
  margin-top: 20px;
}
.tm-summary-stats .tm-summary-table th,
.tm-summary-stats .tm-summary-table td {
  text-align: center;
  vertical-align: middle;
}
</style>
